import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ScanSearch,
  Info,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle2,
  Ban,
} from "lucide-react";

/**
 * Detection Overrides card — per-tenant table of ARP-DisplayName
 * overrides used when generating the Intune detection script.
 *
 * Solves the class of bug where a vendor's installer writes a
 * different DisplayName to `HKLM\...\Uninstall` than the packaged
 * name (e.g. Adobe rebranded the 64-bit Reader ~2023 so the ARP
 * entry became "Adobe Acrobat (64-bit)" — the packaged name
 * "Adobe Acrobat Reader DC (64-bit)" no longer matches).
 *
 * A built-in fallback for the Adobe rebrand is baked into the
 * backend (`server._override_detection_app_name`) — tenants only
 * need this table if they hit ANOTHER vendor doing the same
 * thing (Java, Notepad++ on some channels, etc.).
 */
const emptyForm = {
  id: null,
  match_pattern: "",
  arp_display_name: "",
  description: "",
  enabled: true,
};

export default function DetectionOverridesCard() {
  const { getAccessToken } = useAuth();
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAccessToken]);

  const fetchOverrides = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const { data } = await axios.get(
        `${API}/settings/detection-overrides`,
        { headers }
      );
      setOverrides(data?.overrides || []);
    } catch (e) {
      // Non-fatal: table just stays empty; the built-in Adobe
      // rule still fires server-side so packages keep working.
      console.error("Failed to load detection overrides:", e);
      toast.error(
        e?.response?.data?.detail || "Failed to load detection overrides"
      );
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      match_pattern: row.match_pattern,
      arp_display_name: row.arp_display_name,
      description: row.description || "",
      enabled: row.enabled !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmedPattern = form.match_pattern.trim();
    const trimmedArp = form.arp_display_name.trim();
    if (!trimmedPattern || !trimmedArp) {
      toast.error("Match pattern and ARP DisplayName are both required");
      return;
    }
    setSaving(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const payload = {
        match_pattern: trimmedPattern,
        arp_display_name: trimmedArp,
        description: form.description?.trim() || null,
        enabled: !!form.enabled,
      };
      if (form.id) {
        await axios.put(
          `${API}/settings/detection-overrides/${form.id}`,
          payload,
          { headers }
        );
        toast.success("Detection override updated");
      } else {
        await axios.post(
          `${API}/settings/detection-overrides`,
          payload,
          { headers }
        );
        toast.success("Detection override added");
      }
      setDialogOpen(false);
      setForm(emptyForm);
      await fetchOverrides();
    } catch (e) {
      console.error("Save failed:", e);
      toast.error(
        e?.response?.data?.detail || "Failed to save detection override"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    // Confirm via native prompt — matches other destructive settings
    // actions in this page (avoid pulling in yet another modal).
    if (
      !window.confirm(
        `Delete detection override for "${row.match_pattern}"? This affects future package builds only.`
      )
    ) {
      return;
    }
    setDeletingId(row.id);
    try {
      const headers = await authHeaders();
      await axios.delete(
        `${API}/settings/detection-overrides/${row.id}`,
        { headers }
      );
      toast.success("Detection override deleted");
      await fetchOverrides();
    } catch (e) {
      console.error("Delete failed:", e);
      toast.error(
        e?.response?.data?.detail || "Failed to delete detection override"
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card data-testid="detection-overrides-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <ScanSearch className="h-5 w-5" />
          Detection Overrides
        </CardTitle>
        <CardDescription>
          Retarget the Intune detection script&apos;s <code>$AppName</code> at the{" "}
          DisplayName the installer actually writes to Add/Remove Programs.
          Use this when the packaged product name doesn&apos;t match the ARP entry
          (e.g. Adobe Acrobat&apos;s 2023 rebrand).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>How this works</AlertTitle>
          <AlertDescription className="mt-2 space-y-2 text-sm">
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Match pattern</strong> is searched (case-insensitive)
                as a substring inside the packaged app name.
              </li>
              <li>
                <strong>ARP DisplayName</strong> replaces{" "}
                <code>$AppName</code> in the generated Intune detection
                script only. The PSADT install name is unchanged.
              </li>
              <li>
                The first <strong>enabled</strong> rule that matches wins.
                Rules are evaluated in the order they were created.
              </li>
              <li>
                A built-in rule for <strong>Adobe Acrobat Reader DC (64-bit)</strong>{" "}
                → <code>Adobe Acrobat (64-bit)</code> is always active as a
                fallback. Add a rule here to shadow it.
              </li>
            </ul>
          </AlertDescription>
        </Alert>

        <div className="flex justify-end">
          <Button
            onClick={openCreate}
            data-testid="add-detection-override-btn"
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Override
          </Button>
        </div>

        {loading ? (
          <div
            className="flex items-center justify-center py-10 text-white/70"
            data-testid="detection-overrides-loading"
          >
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading overrides…
          </div>
        ) : overrides.length === 0 ? (
          <div
            className="text-center py-10 text-muted-foreground text-sm border rounded-lg"
            data-testid="detection-overrides-empty"
          >
            No tenant-configured overrides. The built-in Adobe Acrobat rule
            is still active.
          </div>
        ) : (
          <div className="rounded-lg border overflow-hidden" data-testid="detection-overrides-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Match pattern (contains)</TableHead>
                  <TableHead>ARP DisplayName → $AppName</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overrides.map((row) => (
                  <TableRow key={row.id} data-testid={`detection-override-row-${row.id}`}>
                    <TableCell className="font-mono text-sm">
                      {row.match_pattern}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {row.arp_display_name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                      {row.description || "—"}
                    </TableCell>
                    <TableCell>
                      {row.enabled ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200 gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <Ban className="h-3 w-3" />
                          Disabled
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(row)}
                        data-testid={`edit-detection-override-${row.id}`}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(row)}
                        disabled={deletingId === row.id}
                        data-testid={`delete-detection-override-${row.id}`}
                        title="Delete"
                      >
                        {deletingId === row.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-red-500" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent data-testid="detection-override-dialog">
            <DialogHeader>
              <DialogTitle>
                {form.id ? "Edit Detection Override" : "Add Detection Override"}
              </DialogTitle>
              <DialogDescription>
                Rewrite the <code>$AppName</code> used by the Intune detection
                script when the packaged product name doesn&apos;t match the ARP
                entry the installer writes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="det-override-pattern">
                  Match pattern (case-insensitive substring)
                </Label>
                <Input
                  id="det-override-pattern"
                  data-testid="detection-override-pattern-input"
                  placeholder="e.g. Adobe Acrobat Reader"
                  value={form.match_pattern}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, match_pattern: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Any packaged app name that <em>contains</em> this text will
                  match. Matching is case-insensitive.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="det-override-arp">ARP DisplayName</Label>
                <Input
                  id="det-override-arp"
                  data-testid="detection-override-arp-input"
                  placeholder="e.g. Adobe Acrobat (64-bit)"
                  value={form.arp_display_name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      arp_display_name: e.target.value,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  The string that will be written to <code>$AppName</code>{" "}
                  in the generated PowerShell detection script.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="det-override-desc">
                  Description <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="det-override-desc"
                  data-testid="detection-override-description-input"
                  placeholder="Why this override exists — helps future admins."
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={2}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Enabled</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Disabled rules are ignored by the detection generator.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setForm((f) => ({ ...f, enabled: !f.enabled }))
                  }
                  data-testid="detection-override-enabled-toggle"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.enabled ? "bg-green-500" : "bg-gray-300"
                  }`}
                  aria-pressed={form.enabled}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form.enabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
                data-testid="detection-override-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="detection-override-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : form.id ? (
                  "Save Changes"
                ) : (
                  "Add Override"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
